import { Subject, of, combineLatest, iif, throwError } from 'rxjs';
import { takeUntil, catchError, share, map, tap, filter, switchMap, delay, finalize } from 'rxjs/operators';
import { __decorate, __param } from 'tslib';
import { ɵɵdefineInjectable, ɵɵinject, Injectable, NgModule, Inject, SecurityContext, Pipe } from '@angular/core';
import { createAction, props, Store, createSelector, createReducer, on, select } from '@ngrx/store';
import { HttpClient, HttpResponse, HttpErrorResponse, HttpRequest, HttpParams, HttpEventType, HTTP_INTERCEPTORS, HttpHeaders } from '@angular/common/http';
import * as _ from 'lodash';
import { assign, merge } from 'lodash';
import { TranslateModule, TranslateLoader, MissingTranslationHandler } from '@ngx-translate/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';

class SubscriberManager {
    constructor() {
        this.checkUnsubscribeSubject = new Subject();
        this.checkUnsubscribeSubjectsContainer = [];
    }
    resetSubscribers(resetControlSubject) {
        this.resetCheckSubject(this.checkUnsubscribeSubject, false);
        if (resetControlSubject) {
            this.checkUnsubscribeSubject = new Subject();
        }
        this.checkUnsubscribeSubjectsContainer.forEach(elem => {
            this.resetCheckSubject(elem, false);
        });
        this.checkUnsubscribeSubjectsContainer = [];
    }
    resetCheckSubject(subject, removeFromContainer) {
        if (!!subject) {
            subject.next();
            subject.complete();
            if (removeFromContainer) {
                const index = this.checkUnsubscribeSubjectsContainer.indexOf(subject);
                if (index > -1) {
                    this.checkUnsubscribeSubjectsContainer.splice(index, 1);
                }
            }
        }
    }
    takeUntil() {
        return takeUntil(this.checkUnsubscribeSubject);
    }
}

/**
 * Classe astratta per la gestione dei componenti.
 * Include il reset degli operatori in takeUntil() all'onDestroy del componente.
 */
class AbstractComponentClass extends SubscriberManager {
    constructor(viewContainerRef) {
        super();
        this.viewContainerRef = viewContainerRef;
    }
    ngOnDestroy() {
        this.resetSubscribers(false);
        if (this.viewContainerRef) {
            this.clearEmbeddedViews();
            this.viewContainerRef.clear();
        }
    }
    clearEmbeddedViews() {
        if (this.viewContainerRef.length > 0) {
            for (let i = 0; i < this.viewContainerRef.length; i++) {
                this.viewContainerRef.get(i).destroy();
                this.viewContainerRef.detach(i);
                this.viewContainerRef.remove(i);
            }
        }
    }
}

class AbstractContainerClass extends AbstractComponentClass {
    constructor(viewContainerRef, translateService, incrementalJSONStorageService) {
        super(viewContainerRef);
        this.translateService = translateService;
        this.incrementalJSONStorageService = incrementalJSONStorageService;
        this.currentBrowserLang = this.translateService.getBrowserLang();
        this.currentBrowserCultureLang = this.translateService.getBrowserCultureLang();
        const completeTranslationPath = `${this.getTranslationPath()}${this.currentBrowserLang}.json`;
        if (this.incrementalJSONStorageService.checkManagePath(completeTranslationPath)) {
            this.translateService.reloadLang(this.currentBrowserLang);
        }
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        this.resetSubscribers(true);
    }
}

/**
 * Classe astratta per la gestione delle directive.
 * Include il reset degli operatori in takeUntil() all'onDestroy della direttiva.
 */
class AbstractDirectiveClass extends SubscriberManager {
    constructor(viewContainerRef) {
        super();
        this.viewContainerRef = viewContainerRef;
    }
    ngOnDestroy() {
        this.resetSubscribers(false);
        if (this.viewContainerRef) {
            this.clearEmbeddedViews();
            this.viewContainerRef.clear();
        }
        this.viewContainerRef = undefined;
    }
    clearEmbeddedViews() {
        if (this.viewContainerRef.length > 0) {
            for (let i = 0; i < this.viewContainerRef.length; i++) {
                this.viewContainerRef.get(i).destroy();
                this.viewContainerRef.remove(i);
            }
        }
    }
}

function retrieveUserTokenInformations(token) {
    if (token) {
        const baseUrl = token.split('.')[1];
        if (baseUrl) {
            const base = baseUrl.replace('-', '+').replace('_', '/');
            return JSON.parse(window.atob(base));
        }
    }
    return null;
}

const incrementCounter = createAction('[Interceptor] Increment Counter');
const decrementCount = createAction('[Interceptor] Decrement Counter');
const decrementCountDelayed = createAction('[Interceptor] Decrement Counter Delayed');
const incrementPublicCounter = createAction('[Interceptor] Increment Public Counter');
const decrementPublicCount = createAction('[Interceptor] Decrement Public Counter');
const checkToken = createAction('[Interceptor] Get Token');
const updateToken = createAction('[Interceptor] Update Token', props());
const deleteToken = createAction('[Interceptor] Delete Token');
const setUsername = createAction('[Interceptor] Set Username', props());
const internalServerError = createAction('[Interceptor] Internal Server Error');
const pageNotFound = createAction('[Interceptor] Page Not Found');
const setPublicPageState = createAction('[Page] Set Public Page State');
const removePublicPageState = createAction('[Page] Remove Public Page State');

let UserAuthenticationService = class UserAuthenticationService {
    constructor(appState$) {
        this.appState$ = appState$;
    }
    isUserLogged() {
        const localStorageToken = window.localStorage.getItem('smallPayUserToken');
        if (!!localStorageToken) {
            const userToken = retrieveUserTokenInformations(localStorageToken);
            const thousand = 1000;
            const date = new Date().getTime() / thousand;
            if (userToken.exp < date) {
                this.deleteToken();
                window.location.reload();
            }
            return userToken.exp > date;
        }
        return false;
    }
    deleteToken() {
        this.appState$.dispatch(deleteToken());
    }
};
UserAuthenticationService.ctorParameters = () => [
    { type: Store }
];
UserAuthenticationService.ɵprov = ɵɵdefineInjectable({ factory: function UserAuthenticationService_Factory() { return new UserAuthenticationService(ɵɵinject(Store)); }, token: UserAuthenticationService, providedIn: "root" });
UserAuthenticationService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], UserAuthenticationService);

let IncrementalJsonStorageService = class IncrementalJsonStorageService {
    constructor(http) {
        this.http = http;
        this.paths = [];
        this.currentRequests = Array();
        this.stableStore = {};
    }
    checkManagePath(newPath) {
        if (!this.paths.includes(newPath)) {
            this.paths.push(newPath);
            this.currentRequests.push(this.http.get(newPath).pipe(catchError(() => {
                this.paths.splice(this.paths.indexOf(newPath, 0), 1);
                return of({});
            }), share()));
            this.currentObservable = combineLatest(this.currentRequests).pipe(map(response => assign.apply(_, response)), map(completeResponse => merge(completeResponse, this.stableStore)));
            this.currentObservable.subscribe(completeStore => {
                this.stableStore = completeStore;
                this.resetData();
            });
            return true;
        }
        return false;
    }
    getStorage() {
        return this.currentObservable || of(this.stableStore);
    }
    getStableStorage() {
        return this.stableStore;
    }
    resetData() {
        this.currentRequests = new Array();
        this.currentObservable = undefined;
    }
};
IncrementalJsonStorageService.ctorParameters = () => [
    { type: HttpClient }
];
IncrementalJsonStorageService = __decorate([
    Injectable()
], IncrementalJsonStorageService);

class IncrementalTranslateHTTPLoader {
    constructor(loaderService) {
        this.loaderService = loaderService;
    }
    getTranslation() {
        return this.loaderService.getStorage();
    }
}

function incrementalTranslateHTTPLoaderFactory(translateLoaderService) {
    return new IncrementalTranslateHTTPLoader(translateLoaderService);
}

let SpMissingTranslationHandler = class SpMissingTranslationHandler {
    constructor(incrementalJSONStorage) {
        this.incrementalJSONStorage = incrementalJSONStorage;
    }
    handle(params) {
        const key = params.key;
        try {
            const translation = key.split('.').reduce((o, i) => o[i], this.incrementalJSONStorage.getStableStorage());
            return translation || key;
        }
        catch (error) {
            return key;
        }
    }
};
SpMissingTranslationHandler.ctorParameters = () => [
    { type: IncrementalJsonStorageService }
];
SpMissingTranslationHandler = __decorate([
    Injectable()
], SpMissingTranslationHandler);

const ɵ0 = incrementalTranslateHTTPLoaderFactory;
let InternationalizationModule = class InternationalizationModule {
};
InternationalizationModule = __decorate([
    NgModule({
        declarations: [],
        imports: [
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: ɵ0,
                    deps: [IncrementalJsonStorageService]
                },
                missingTranslationHandler: { provide: MissingTranslationHandler, useClass: SpMissingTranslationHandler }
            })
        ],
        providers: [IncrementalJsonStorageService]
    })
], InternationalizationModule);

const selectFeature = appState => appState.token;
const ɵ0$1 = selectFeature;
const ɵ1 = (state) => state ? state.internalLoaderCounter : undefined;
const selectLoaderCount = createSelector(selectFeature, ɵ1);
const ɵ2 = (state) => state ? state.loaderCounter : undefined;
const selectPublicLoaderCount = createSelector(selectFeature, ɵ2);
const ɵ3 = (state) => state ? state.userToken : undefined;
const selectUserToken = createSelector(selectFeature, ɵ3);
const ɵ4 = (state) => state ? retrieveUserTokenInformations(state.userToken) : undefined;
const selectUserInformation = createSelector(selectFeature, ɵ4);
const ɵ5 = (state) => state ? state.username : undefined;
const selectUserUsername = createSelector(selectFeature, ɵ5);

let HttpRequestInterceptorService = class HttpRequestInterceptorService {
    constructor(config, appState$) {
        this.config = config;
        this.appState$ = appState$;
        this.appState$.select(selectUserToken)
            .subscribe(token => {
            this.userToken = token;
        });
    }
    intercept(req, next) {
        if (req.url.startsWith(this.config.baseUrl) && !!this.userToken) {
            req = req.clone({
                setHeaders: {
                    Authorization: this.userToken
                }
            });
        }
        return next.handle(req);
    }
};
HttpRequestInterceptorService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: ['config',] }] },
    { type: Store }
];
HttpRequestInterceptorService = __decorate([
    Injectable(),
    __param(0, Inject('config'))
], HttpRequestInterceptorService);

const HTTP_CODE = {
    NOT_FOUND: 404,
    INTERNAL_SERVER_ERROR: 500
};
let HttpResponseInterceptorService = class HttpResponseInterceptorService {
    constructor(config, appState$) {
        this.config = config;
        this.appState$ = appState$;
    }
    intercept(req, next) {
        if (req.url.startsWith(this.config.baseUrl)) {
            return next.handle(req).pipe(tap(event => {
                if (event instanceof HttpResponse) {
                    const newToken = event.headers.get('Authorization');
                    this.appState$.dispatch(updateToken({ userToken: newToken }));
                }
            }, event => {
                if (event instanceof HttpErrorResponse) {
                    if (event.status === HTTP_CODE.INTERNAL_SERVER_ERROR) {
                        this.appState$.dispatch(internalServerError());
                    }
                    if (event.status === HTTP_CODE.NOT_FOUND) {
                        this.appState$.dispatch(pageNotFound());
                    }
                }
            }));
        }
        return next.handle(req);
    }
};
HttpResponseInterceptorService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: ['config',] }] },
    { type: Store }
];
HttpResponseInterceptorService = __decorate([
    Injectable(),
    __param(0, Inject('config'))
], HttpResponseInterceptorService);

let TokenInterceptorEffects = class TokenInterceptorEffects {
    constructor(actions$) {
        this.actions$ = actions$;
        this.decrementCounterDelay = 300;
        this.getToken$ = createEffect(() => this.actions$.pipe(ofType(checkToken), map(() => window.localStorage.getItem('smallPayUserToken')), filter(token => !!token), switchMap(token => iif(() => token === 'null', of(deleteToken()), of(updateToken({ userToken: token }))))));
        this.updateToken$ = createEffect(() => this.actions$.pipe(ofType(updateToken), tap(newToken => window.localStorage.setItem('smallPayUserToken', newToken.userToken))), { dispatch: false });
        this.deleteToken$ = createEffect(() => this.actions$.pipe(ofType(deleteToken), tap(() => {
            window.localStorage.removeItem('smallPayUserToken');
        })), { dispatch: false });
        this.decrementCount$ = createEffect(() => this.actions$.pipe(ofType(decrementCount), delay(this.decrementCounterDelay), map(() => decrementCountDelayed())));
    }
};
TokenInterceptorEffects.ctorParameters = () => [
    { type: Actions }
];
TokenInterceptorEffects = __decorate([
    Injectable()
], TokenInterceptorEffects);

const initialState = {
    internalLoaderCounter: 0,
    loaderCounter: 0,
    userToken: null,
    username: null,
};
const ɵ0$2 = state => {
    return Object.assign(Object.assign({}, state), { internalLoaderCounter: state.internalLoaderCounter + 1 });
}, ɵ1$1 = state => {
    return Object.assign(Object.assign({}, state), { internalLoaderCounter: state.internalLoaderCounter - 1 });
}, ɵ2$1 = state => {
    return Object.assign(Object.assign({}, state), { loaderCounter: state.loaderCounter + 1 });
}, ɵ3$1 = state => {
    return Object.assign(Object.assign({}, state), { loaderCounter: state.loaderCounter - 1 });
}, ɵ4$1 = (state, props) => {
    const userToken = retrieveUserTokenInformations(props.userToken);
    return Object.assign(Object.assign({}, state), { userToken: props.userToken, username: userToken ? userToken.sub : null });
}, ɵ5$1 = state => {
    return Object.assign(Object.assign({}, state), { userToken: null });
};
const interceptorReducer = createReducer(initialState, on(incrementCounter, ɵ0$2), on(decrementCountDelayed, ɵ1$1), on(incrementPublicCounter, ɵ2$1), on(decrementPublicCount, ɵ3$1), on(updateToken, ɵ4$1), on(deleteToken, ɵ5$1));
function reducer(state, action) {
    return interceptorReducer(state, action);
}

const applications = [
    'app-smallpay'
];

class AbstractRestService {
    constructor(httpCommunicationsService) {
        this.httpCommunicationsService = httpCommunicationsService;
    }
    get(queryParams, flagShowLoader = true, subPath = '', responseType, headers) {
        return this.httpCommunicationsService.retrieveGetCall(this.path + subPath, flagShowLoader, queryParams, responseType, headers);
    }
    retrieveAll(queryParams, subPath = '', headers) {
        return this.httpCommunicationsService.retrieveGetCall(this.path + subPath, true, queryParams, null, headers);
    }
    post(model, subPath = '', loader = true, headers, responseType) {
        return this.httpCommunicationsService.retrievePostCall(this.path + subPath, model, loader, headers, responseType);
    }
    put(model, subPath = '') {
        return this.httpCommunicationsService.retrievePutCall(this.path + subPath, model);
    }
    putAll(model, subPath = '') {
        return this.httpCommunicationsService.retrievePutCall(this.path + subPath, model);
    }
    delete(id, headers) {
        return this.httpCommunicationsService.retrieveDeleteCall(this.path.concat(`?id=${id}`), true, headers);
    }
}

let HttpCommunicationsService = class HttpCommunicationsService {
    constructor(config, httpClient, appState$) {
        this.config = config;
        this.httpClient = httpClient;
        this.appState$ = appState$;
        this.appState$.pipe(select(selectUserToken)).subscribe(token => {
            this.userToken = token;
        });
    }
    retrievePostCall(endpoint, body, loader = true, headers, responseType) {
        return this.retrieveHttpCall(new HttpRequest('POST', this.config.baseUrl + endpoint, body, { headers, responseType: (responseType !== null && responseType !== void 0 ? responseType : 'json') }), loader);
    }
    retrievePutCall(endpoint, body, loader = true) {
        return this.retrieveHttpCall(new HttpRequest('PUT', this.config.baseUrl + endpoint, body), loader);
    }
    retrieveGetCall(endpoint, loader, params = null, responseType, headers) {
        let init = {};
        if (!!params) {
            init = Object.assign(Object.assign({}, init), { params: new HttpParams({
                    fromObject: params
                }) });
        }
        if (!!responseType) {
            init = Object.assign(Object.assign({}, init), { responseType });
        }
        if (!!headers) {
            init = Object.assign(Object.assign({}, init), { headers });
        }
        return this.retrieveHttpCall(new HttpRequest('GET', this.config.baseUrl + endpoint, init), loader);
    }
    retrieveDeleteCall(endpoint, loader = true, headers) {
        return this.retrieveHttpCall(new HttpRequest('DELETE', this.config.baseUrl + endpoint, { headers }), loader);
    }
    retrieveHttpCall(httpRequest, loader) {
        if (loader) {
            this.appState$.dispatch(incrementCounter());
        }
        httpRequest = httpRequest.clone({
            setHeaders: this.retrieveUpdatedHeaders()
        });
        return this.httpClient.request(httpRequest).pipe(filter((response) => response.type === HttpEventType.Response), map((response) => response.body), finalize(() => {
            if (loader) {
                this.appState$.dispatch(decrementCount());
            }
        }));
    }
    retrieveUpdatedHeaders() {
        let updatedHeaders = {};
        if (!!this.userToken) {
            updatedHeaders = Object.assign(Object.assign({}, updatedHeaders), { Authorization: this.userToken });
        }
        return updatedHeaders;
    }
};
HttpCommunicationsService.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Inject, args: ['config',] }] },
    { type: HttpClient },
    { type: Store }
];
HttpCommunicationsService = __decorate([
    Injectable(),
    __param(0, Inject('config'))
], HttpCommunicationsService);

let AuthenticationRestService = class AuthenticationRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/public/user/login';
    }
    login(authenticationRequestInterface) {
        return super.post(authenticationRequestInterface);
    }
};
AuthenticationRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
AuthenticationRestService.ɵprov = ɵɵdefineInjectable({ factory: function AuthenticationRestService_Factory() { return new AuthenticationRestService(ɵɵinject(HttpCommunicationsService)); }, token: AuthenticationRestService, providedIn: "root" });
AuthenticationRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], AuthenticationRestService);

var HttpCommunicationsModule_1;
let HttpCommunicationsModule = HttpCommunicationsModule_1 = class HttpCommunicationsModule {
    static forRoot(config) {
        return {
            ngModule: HttpCommunicationsModule_1,
            providers: [
                HttpCommunicationsService,
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: HttpRequestInterceptorService,
                    multi: true
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: HttpResponseInterceptorService,
                    multi: true
                },
                { provide: 'config', useValue: config }
            ]
        };
    }
};
HttpCommunicationsModule = HttpCommunicationsModule_1 = __decorate([
    NgModule()
], HttpCommunicationsModule);

let BootRestService = class BootRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/notices';
    }
    checkSteps() {
        return super.retrieveAll();
    }
    resolveStep(param) {
        return super.put(param);
    }
};
BootRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
BootRestService.ɵprov = ɵɵdefineInjectable({ factory: function BootRestService_Factory() { return new BootRestService(ɵɵinject(HttpCommunicationsService)); }, token: BootRestService, providedIn: "root" });
BootRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], BootRestService);

let MarketCatalogImgRestService = class MarketCatalogImgRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/catalog/uploadCatalogImage';
    }
    saveCatalogImage(request) {
        return super.post(request.formRequest);
    }
};
MarketCatalogImgRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketCatalogImgRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketCatalogImgRestService_Factory() { return new MarketCatalogImgRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketCatalogImgRestService, providedIn: "root" });
MarketCatalogImgRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketCatalogImgRestService);

let CustomerInstallmentsByClientIdRestService = class CustomerInstallmentsByClientIdRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/payment';
    }
    relaunchAllInstallmentsByCustomerId(param) {
        return super.put(param, '/executeUnpaidInstallmentsByClientId');
    }
};
CustomerInstallmentsByClientIdRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomerInstallmentsByClientIdRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomerInstallmentsByClientIdRestService_Factory() { return new CustomerInstallmentsByClientIdRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomerInstallmentsByClientIdRestService, providedIn: "root" });
CustomerInstallmentsByClientIdRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomerInstallmentsByClientIdRestService);

let CustomerInstallmentsRestService = class CustomerInstallmentsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/payment';
    }
    relaunchInstallments(rate) {
        return super.put(rate, '/rateListRelaunch');
    }
    reversePayment(paymentId) {
        return super.post({ id: paymentId }, '/requestReversal');
    }
    getCurrentPaymentState(clientId) {
        const request = {
            clientId: clientId.toString(),
            size: '5',
            page: '0',
        };
        return super.get(request);
    }
};
CustomerInstallmentsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomerInstallmentsRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomerInstallmentsRestService_Factory() { return new CustomerInstallmentsRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomerInstallmentsRestService, providedIn: "root" });
CustomerInstallmentsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomerInstallmentsRestService);

let CustomerPaymentsPaginableRestService = class CustomerPaymentsPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/rate';
    }
    retrieveCustomerPayments(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                case 'clientId':
                case 'serviceId':
                    partial[key] = param[key].toFixed(0);
                    break;
                case 'paidInCash':
                    if (param.paidInCash !== undefined) {
                        partial[key] = param[key] === null ? '' : param[key];
                    }
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
CustomerPaymentsPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomerPaymentsPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomerPaymentsPaginableRestService_Factory() { return new CustomerPaymentsPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomerPaymentsPaginableRestService, providedIn: "root" });
CustomerPaymentsPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomerPaymentsPaginableRestService);

let CustomerPaymentsRestService = class CustomerPaymentsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/rate';
    }
    retrieveCustomerPaymentbyId(req) {
        return super.retrieveAll({
            id: String(req.id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    updatePayment(req) {
        return super.put(req, '/editNote');
    }
    payPerCash(rate) {
        return super.put(rate, '/alreadyPayed');
    }
    deleteRate(rate) {
        return super.put(rate, '/deleteRate');
    }
    deleteRateByClientId(param) {
        return super.put(param, '/concludeClient');
    }
};
CustomerPaymentsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomerPaymentsRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomerPaymentsRestService_Factory() { return new CustomerPaymentsRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomerPaymentsRestService, providedIn: "root" });
CustomerPaymentsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomerPaymentsRestService);

function reduceToMap(map) {
    return Array.from(map).reduce((obj, [key, value]) => {
        obj[key] = value;
        return obj;
    }, {});
}

let CustomersPaginableRestService = class CustomersPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/client';
    }
    retrieveCustomers(customersPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(customersPaginableRequestInterface.size));
        map.set('page', String(customersPaginableRequestInterface.page));
        map.set('localUnity', String(customersPaginableRequestInterface.localUnity));
        this.setIfPresent(map, 'sortBy', customersPaginableRequestInterface.sortBy);
        this.setIfPresent(map, 'sortingMethod', customersPaginableRequestInterface.sortingMethod);
        this.setIfPresent(map, 'nominative', customersPaginableRequestInterface.nominative);
        this.setIfPresent(map, 'surname', customersPaginableRequestInterface.surname);
        this.setIfPresent(map, 'email', customersPaginableRequestInterface.email);
        this.setIfPresent(map, 'state', customersPaginableRequestInterface.state);
        this.setIfPresent(map, 'dateStart', customersPaginableRequestInterface.dateStart);
        this.setIfPresent(map, 'service.name', customersPaginableRequestInterface.product);
        this.setIfPresent(map, 'dateEnd', customersPaginableRequestInterface.dateEnd);
        this.setIfPresent(map, 'channel', customersPaginableRequestInterface.channel);
        return super.get(reduceToMap(map), false);
    }
    setIfPresent(map, key, value) {
        if (value == null || value == undefined)
            return;
        const stringValue = String(value);
        if (stringValue.trim() === '')
            return;
        map.set(key, stringValue);
    }
};
CustomersPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomersPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomersPaginableRestService_Factory() { return new CustomersPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomersPaginableRestService, providedIn: "root" });
CustomersPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomersPaginableRestService);

let CustomersRestService = class CustomersRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/client';
    }
    retrieveCustomerById(customerReqInterface) {
        return super.retrieveAll({
            id: String(customerReqInterface.id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    createCustomer(customerRequestInterface) {
        return super.post(customerRequestInterface);
    }
    contractualizeSelling(contractualizationPayload) {
        return super.post(contractualizationPayload, '/contractualizeClient');
    }
    updateCustomer(customerRequestInterface) {
        return super.put(customerRequestInterface);
    }
    sendEmailContract(param) {
        return super.get({ clientId: `${param.clientId}`, serviceId: `${param.serviceId}` }, true, '/sendEmailContract');
    }
    unsubscribePerpetual(param) {
        return super.post({ id: param.id }, '/perpetualUnSubscribe');
    }
    downloadCsv(filters) {
        const param = this.produceFilterMap(filters);
        return super.get(param, true, '/export', 'blob');
    }
    produceFilterMap(param) {
        return Object.keys(param).reduce((partial, key) => {
            if (param[key] !== undefined && param[key] !== null) {
                partial[key] = `${param[key]}`;
            }
            return partial;
        }, {});
    }
};
CustomersRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CustomersRestService.ɵprov = ɵɵdefineInjectable({ factory: function CustomersRestService_Factory() { return new CustomersRestService(ɵɵinject(HttpCommunicationsService)); }, token: CustomersRestService, providedIn: "root" });
CustomersRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CustomersRestService);

const customerStates = [
    'ATTIVO',
    'ELIMINATO',
    'IN ATTIVAZIONE',
    'IN ATTESA GTW',
    'INSOLVENTE',
    'NON ATTIVO',
    'CONCLUSO',
    'DA TOKENIZZARE'
];

var PaymentChannels;
(function (PaymentChannels) {
    PaymentChannels["CONSORZIO"] = "CONSORZIO";
    PaymentChannels["NEXI"] = "NEXI";
    PaymentChannels["NEXI_PAYPAL"] = "NEXI_PAYPAL";
    PaymentChannels["NEXI_3_PAYPAL"] = "NEXI_3_PAYPAL";
    PaymentChannels["NEXI_PAGODIL"] = "NEXI_PAGODIL";
    PaymentChannels["STRIPE"] = "STRIPE";
    PaymentChannels["STRIPE_SDD"] = "STRIPE_SDD";
    PaymentChannels["STRIPE_KLARNA"] = "STRIPE_KLARNA";
    PaymentChannels["STRIPE_ALMA"] = "STRIPE_ALMA";
    PaymentChannels["STRIPE_SCALAPAY"] = "STRIPE_SCALAPAY";
    PaymentChannels["SCALAPAY"] = "SCALAPAY";
    PaymentChannels["APPPAGO"] = "APPPAGO";
    PaymentChannels["KLARNA"] = "KLARNA";
    PaymentChannels["QOMODO"] = "QOMODO";
})(PaymentChannels || (PaymentChannels = {}));

let DashboardMarketRestService = class DashboardMarketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/dashboard';
    }
    retrieveDashboard(dashboardMarketRequestInterface) {
        const map = new Map();
        map.set('localUnityList', dashboardMarketRequestInterface.localUnits
            .map(localUnit => String(localUnit))
            .join(','));
        return super.retrieveAll(reduceToMap(map));
    }
};
DashboardMarketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
DashboardMarketRestService.ɵprov = ɵɵdefineInjectable({ factory: function DashboardMarketRestService_Factory() { return new DashboardMarketRestService(ɵɵinject(HttpCommunicationsService)); }, token: DashboardMarketRestService, providedIn: "root" });
DashboardMarketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], DashboardMarketRestService);

let DownloadWordDocsService = class DownloadWordDocsService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/document';
    }
    retrieveContrattoEndUser(serviceId) {
        return super.get({ serviceId }, true, '/getcontrattoenduser');
    }
    getCondgensw() {
        return super.get(undefined, true, '/getcondgensw');
    }
    getOrdineAccount() {
        return super.get(undefined, true, '/getordineaccount');
    }
    retrieveOrdineServizio(servicesmallpayId) {
        return super.get({ servicesmallpayId }, true, '/getordineservizio');
    }
};
DownloadWordDocsService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
DownloadWordDocsService.ɵprov = ɵɵdefineInjectable({ factory: function DownloadWordDocsService_Factory() { return new DownloadWordDocsService(ɵɵinject(HttpCommunicationsService)); }, token: DownloadWordDocsService, providedIn: "root" });
DownloadWordDocsService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], DownloadWordDocsService);

let GatewayRestService = class GatewayRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/gateway';
    }
    retrieveGateways(param) {
        const localMap = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(localMap, false);
    }
    getMerchantActiveGateways(merchantId) {
        const params = merchantId ? { merchantId: `${merchantId}` } : null;
        return super.retrieveAll(params, '/activeGateways');
    }
    updateGateways(gatewaysRequestInterface) {
        return super.putAll([...gatewaysRequestInterface], '/updateGateways');
    }
    updateGateway(gatewaysRequestInterface) {
        return super.put(gatewaysRequestInterface);
    }
    deleteGateway(id) {
        return super.delete(id);
    }
    resumeOnBoarding(id) {
        return super.get({ id }, true, '/resumeOnBoarding');
    }
    retrieveGatewayById(id) {
        return super.retrieveAll({
            id: `${id}`
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
};
GatewayRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
GatewayRestService.ɵprov = ɵɵdefineInjectable({ factory: function GatewayRestService_Factory() { return new GatewayRestService(ɵɵinject(HttpCommunicationsService)); }, token: GatewayRestService, providedIn: "root" });
GatewayRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], GatewayRestService);

let GeolocationRestService = class GeolocationRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/public';
    }
    getStores() {
        return super.get({}, false, '/stores');
    }
};
GeolocationRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
GeolocationRestService.ɵprov = ɵɵdefineInjectable({ factory: function GeolocationRestService_Factory() { return new GeolocationRestService(ɵɵinject(HttpCommunicationsService)); }, token: GeolocationRestService, providedIn: "root" });
GeolocationRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], GeolocationRestService);

let LocalUnitPaginableRestService = class LocalUnitPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/localunity';
    }
    retrieveLocalUnits(localUnitPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(localUnitPaginableRequestInterface.size));
        map.set('page', String(localUnitPaginableRequestInterface.page));
        if (!!localUnitPaginableRequestInterface.sortBy) {
            map.set('sortBy', localUnitPaginableRequestInterface.sortBy);
        }
        if (!!localUnitPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', localUnitPaginableRequestInterface.sortingMethod);
        }
        if (!!localUnitPaginableRequestInterface.name) {
            map.set('name', localUnitPaginableRequestInterface.name);
        }
        if (!!localUnitPaginableRequestInterface.address) {
            map.set('address', localUnitPaginableRequestInterface.address);
        }
        if (!!localUnitPaginableRequestInterface.city) {
            map.set('city', localUnitPaginableRequestInterface.city);
        }
        if (!!localUnitPaginableRequestInterface.label) {
            map.set('label', localUnitPaginableRequestInterface.label);
        }
        if (!!localUnitPaginableRequestInterface.province) {
            map.set('province', localUnitPaginableRequestInterface.province);
        }
        if (!!localUnitPaginableRequestInterface.signboard) {
            map.set('signboard', localUnitPaginableRequestInterface.signboard);
        }
        return super.get(reduceToMap(map), false);
    }
};
LocalUnitPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
LocalUnitPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function LocalUnitPaginableRestService_Factory() { return new LocalUnitPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: LocalUnitPaginableRestService, providedIn: "root" });
LocalUnitPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], LocalUnitPaginableRestService);

let LocalUnitQrCodeRestService = class LocalUnitQrCodeRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/localunity';
    }
    getQrCode(request) {
        return super.post(request, '/getQrCode', true, undefined, 'blob');
    }
};
LocalUnitQrCodeRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
LocalUnitQrCodeRestService.ɵprov = ɵɵdefineInjectable({ factory: function LocalUnitQrCodeRestService_Factory() { return new LocalUnitQrCodeRestService(ɵɵinject(HttpCommunicationsService)); }, token: LocalUnitQrCodeRestService, providedIn: "root" });
LocalUnitQrCodeRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], LocalUnitQrCodeRestService);

let LocalUnitRestService = class LocalUnitRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/localunity';
    }
    getAllLocalUnits() {
        return super.retrieveAll();
    }
    retrieveLocalUnitById(localUnitRequestInterface) {
        return super.retrieveAll({
            id: String(localUnitRequestInterface.id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    createUnit(unitRequestInterface) {
        return super.post(unitRequestInterface);
    }
    updateUnit(unitRequestInterface) {
        return super.put(unitRequestInterface);
    }
};
LocalUnitRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
LocalUnitRestService.ɵprov = ɵɵdefineInjectable({ factory: function LocalUnitRestService_Factory() { return new LocalUnitRestService(ɵɵinject(HttpCommunicationsService)); }, token: LocalUnitRestService, providedIn: "root" });
LocalUnitRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], LocalUnitRestService);

let ForgotPasswordService = class ForgotPasswordService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/public/password';
    }
    retrieveUrl(email) {
        return this.post({ email }, '/forgotPassword');
    }
    newPassword(headers) {
        return this.post({}, '/changePassword', true, new HttpHeaders({ token: headers.token, password: headers.password }));
    }
};
ForgotPasswordService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ForgotPasswordService.ɵprov = ɵɵdefineInjectable({ factory: function ForgotPasswordService_Factory() { return new ForgotPasswordService(ɵɵinject(HttpCommunicationsService)); }, token: ForgotPasswordService, providedIn: "root" });
ForgotPasswordService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ForgotPasswordService);

let MarketMerchantImgRestService = class MarketMerchantImgRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/merchant/image';
    }
    saveMerchantImage(img) {
        return super.put(img.file);
    }
};
MarketMerchantImgRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketMerchantImgRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketMerchantImgRestService_Factory() { return new MarketMerchantImgRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketMerchantImgRestService, providedIn: "root" });
MarketMerchantImgRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketMerchantImgRestService);

let MarketMerchantRestService = class MarketMerchantRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/merchant';
    }
    getLoggedMerchant() {
        return super.retrieveAll().pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
};
MarketMerchantRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketMerchantRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketMerchantRestService_Factory() { return new MarketMerchantRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketMerchantRestService, providedIn: "root" });
MarketMerchantRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketMerchantRestService);

let RegistryCreditCardPaginableRestService = class RegistryCreditCardPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/creditCard';
    }
    retrieveCreditCards(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                case 'expiryYear':
                    if (!!param.expiryYear) {
                        const yearIndexAndLength = 2;
                        partial[key] = `${param.expiryYear}`.substr(yearIndexAndLength, yearIndexAndLength);
                    }
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
RegistryCreditCardPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
RegistryCreditCardPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function RegistryCreditCardPaginableRestService_Factory() { return new RegistryCreditCardPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: RegistryCreditCardPaginableRestService, providedIn: "root" });
RegistryCreditCardPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RegistryCreditCardPaginableRestService);

let RegistryCreditCardRestService = class RegistryCreditCardRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/creditCard';
    }
    createCreditCard(creditCardRequestInterface) {
        return super.post(creditCardRequestInterface);
    }
    creditCardToNexi() {
        return super.post(null);
    }
    updateCustomerCreditCard(client) {
        return super.put(client, '/changeCard');
    }
    getAllCreditCards() {
        return super.retrieveAll();
    }
    updateCreditCard(creditCardRequestInterface) {
        return super.put(creditCardRequestInterface);
    }
    deleteCreditCard(id) {
        return super.delete(id);
    }
};
RegistryCreditCardRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
RegistryCreditCardRestService.ɵprov = ɵɵdefineInjectable({ factory: function RegistryCreditCardRestService_Factory() { return new RegistryCreditCardRestService(ɵɵinject(HttpCommunicationsService)); }, token: RegistryCreditCardRestService, providedIn: "root" });
RegistryCreditCardRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RegistryCreditCardRestService);

let NotificationMarketRestService = class NotificationMarketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/notifications';
    }
    retrieveNotifications(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                case 'localUnity':
                    partial[key] = param.localUnits.map(localUnit => `${localUnit}`).join(',');
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map);
    }
};
NotificationMarketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
NotificationMarketRestService.ɵprov = ɵɵdefineInjectable({ factory: function NotificationMarketRestService_Factory() { return new NotificationMarketRestService(ɵɵinject(HttpCommunicationsService)); }, token: NotificationMarketRestService, providedIn: "root" });
NotificationMarketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], NotificationMarketRestService);

let PaymentFeesMarketPaginableRestService = class PaymentFeesMarketPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/rate';
    }
    retrieveFees(params) {
        const map = new Map();
        map.set('size', String(params.size));
        map.set('page', String(params.page));
        if (!!params.rataSmallpayId) {
            map.set('rataSmallpayId', String(params.rataSmallpayId));
        }
        else {
            map.set('applicationFeeRataSmallpayId', String(params.applicationFeeRataSmallpayId));
        }
        if (!!params.id) {
            map.set('id', String(params.id));
        }
        if (!!params.state) {
            map.set('state', params.state);
        }
        if (!!params.sortBy) {
            map.set('sortBy', params.sortBy);
        }
        if (!!params.sortingMethod) {
            map.set('sortingMethod', params.sortingMethod);
        }
        return super.get(reduceToMap(map), false);
    }
};
PaymentFeesMarketPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentFeesMarketPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentFeesMarketPaginableRestService_Factory() { return new PaymentFeesMarketPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentFeesMarketPaginableRestService, providedIn: "root" });
PaymentFeesMarketPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentFeesMarketPaginableRestService);

let PaymentServiceMarketRestService = class PaymentServiceMarketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ratesmallpay';
    }
    retrievePaymentInstallments(installmentPaginableReqInterface) {
        return super.retrieveAll({
            id: String(installmentPaginableReqInterface.id)
        });
    }
};
PaymentServiceMarketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentServiceMarketRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentServiceMarketRestService_Factory() { return new PaymentServiceMarketRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentServiceMarketRestService, providedIn: "root" });
PaymentServiceMarketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentServiceMarketRestService);

let PaymentsMarketPaginableRestService = class PaymentsMarketPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentsmallpay';
    }
    retrievePayments(param) {
        const map = new Map();
        map.set('size', String(param.size));
        map.set('page', String(param.page));
        if (!!param.sortBy) {
            map.set('sortBy', param.sortBy);
        }
        if (!!param.sortingMethod) {
            map.set('sortingMethod', param.sortingMethod);
        }
        if (!!param.state) {
            map.set('state', param.state);
        }
        if (!!param.dateCreationStart) {
            map.set('dateCreationStart', param.dateCreationStart);
        }
        if (!!param.dateCreationEnd) {
            map.set('dateCreationEnd', param.dateCreationEnd);
        }
        if (!!param.services) {
            map.set('services', String(param.services));
        }
        if (!!param.dateTransactionStart) {
            map.set('dateTransactionStart', param.dateTransactionStart);
        }
        if (!!param.dateTransactionEnd) {
            map.set('dateTransactionEnd', param.dateTransactionEnd);
        }
        if (!!param.amount) {
            map.set('amount', String(param.amount));
        }
        return super.get(reduceToMap(map), false);
    }
};
PaymentsMarketPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentsMarketPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentsMarketPaginableRestService_Factory() { return new PaymentsMarketPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentsMarketPaginableRestService, providedIn: "root" });
PaymentsMarketPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentsMarketPaginableRestService);

let PaymentsMarketRestService = class PaymentsMarketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentsmallpay';
    }
    retrievePaymentbyId(id) {
        return super.retrieveAll({ id: String(id) }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    proceedToPayUnpaidInstallments(param) {
        return super.put(param, '/proceedToPayUnpaidInstallments');
    }
};
PaymentsMarketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentsMarketRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentsMarketRestService_Factory() { return new PaymentsMarketRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentsMarketRestService, providedIn: "root" });
PaymentsMarketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentsMarketRestService);

let RatesmallpayMarketPaginableRestService = class RatesmallpayMarketPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ratesmallpay';
    }
    retrieveRatesmallpay(rateSmallpayMarketPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(rateSmallpayMarketPaginableRequestInterface.size));
        map.set('page', String(rateSmallpayMarketPaginableRequestInterface.page));
        map.set('idPayment', String(rateSmallpayMarketPaginableRequestInterface.idPayment));
        if (!!rateSmallpayMarketPaginableRequestInterface.sortBy) {
            map.set('sortBy', rateSmallpayMarketPaginableRequestInterface.sortBy);
        }
        if (!!rateSmallpayMarketPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', rateSmallpayMarketPaginableRequestInterface.sortingMethod);
        }
        if (!!rateSmallpayMarketPaginableRequestInterface.amountRate) {
            map.set('amountRate', rateSmallpayMarketPaginableRequestInterface.amountRate);
        }
        if (!!rateSmallpayMarketPaginableRequestInterface.state) {
            map.set('state', rateSmallpayMarketPaginableRequestInterface.state);
        }
        if (!!rateSmallpayMarketPaginableRequestInterface.feeValue) {
            map.set('feeValue', rateSmallpayMarketPaginableRequestInterface.feeValue);
        }
        return super.get(reduceToMap(map), false);
    }
};
RatesmallpayMarketPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
RatesmallpayMarketPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function RatesmallpayMarketPaginableRestService_Factory() { return new RatesmallpayMarketPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: RatesmallpayMarketPaginableRestService, providedIn: "root" });
RatesmallpayMarketPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RatesmallpayMarketPaginableRestService);

const posTypes = [
    'CPOS',
    'HPOS'
];

let PosPaginableRestService = class PosPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/physicalpos';
    }
    retrievePosForLocalUnit(posPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(posPaginableRequestInterface.size));
        map.set('page', String(posPaginableRequestInterface.page));
        map.set('localUnity', String(posPaginableRequestInterface.localUnity));
        if (!!posPaginableRequestInterface.sortBy) {
            map.set('sortBy', posPaginableRequestInterface.sortBy);
        }
        if (!!posPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', posPaginableRequestInterface.sortingMethod);
        }
        if (!!posPaginableRequestInterface.name) {
            map.set('name', posPaginableRequestInterface.name);
        }
        if (!!posPaginableRequestInterface.serial) {
            map.set('serial', posPaginableRequestInterface.serial);
        }
        return super.get(reduceToMap(map), false);
    }
};
PosPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PosPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function PosPaginableRestService_Factory() { return new PosPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: PosPaginableRestService, providedIn: "root" });
PosPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PosPaginableRestService);

let PosRestService = class PosRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/physicalpos';
    }
    configurePhysicalPos(requestInterface) {
        return super.post(requestInterface);
    }
    deletePhysicalPos(pos) {
        return super.delete(pos.id);
    }
    switchPosState(pos) {
        return super.put(pos, '/switch');
    }
    retrievePosForLocalUnit(requestInterface) {
        return super.retrieveAll({ localUnity: String(requestInterface.localUnity) }, '/localunity');
    }
    retrievePosStatus(localUnity, serviceId) {
        const mappa = new Map();
        mappa.set('localUnity', localUnity);
        mappa.set('serviceId', String(serviceId));
        return super.get(reduceToMap(mappa), false, '/POSAvailableStatus');
    }
};
PosRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PosRestService.ɵprov = ɵɵdefineInjectable({ factory: function PosRestService_Factory() { return new PosRestService(ɵɵinject(HttpCommunicationsService)); }, token: PosRestService, providedIn: "root" });
PosRestService = __decorate([
    Injectable({
        providedIn: 'root',
    })
], PosRestService);

let CreateGeneralProductRestService = class CreateGeneralProductRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/catalog';
    }
    createGenericProduct(product) {
        return super.post(product, '/generalProduct');
    }
};
CreateGeneralProductRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CreateGeneralProductRestService.ɵprov = ɵɵdefineInjectable({ factory: function CreateGeneralProductRestService_Factory() { return new CreateGeneralProductRestService(ɵɵinject(HttpCommunicationsService)); }, token: CreateGeneralProductRestService, providedIn: "root" });
CreateGeneralProductRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CreateGeneralProductRestService);

let ProductsPaginableRestService = class ProductsPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/catalog';
    }
    retrieveProducts(productsPaginableRequestInterface, loader = false) {
        const map = new Map();
        map.set('size', String(productsPaginableRequestInterface.size));
        map.set('page', String(productsPaginableRequestInterface.page));
        if (!!productsPaginableRequestInterface.sortBy) {
            map.set('sortBy', productsPaginableRequestInterface.sortBy);
        }
        if (!!productsPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', productsPaginableRequestInterface.sortingMethod);
        }
        if (!!productsPaginableRequestInterface.localUnity) {
            map.set('localUnity', String(productsPaginableRequestInterface.localUnity));
        }
        if (!!productsPaginableRequestInterface.name) {
            map.set('name', productsPaginableRequestInterface.name);
        }
        if (!!productsPaginableRequestInterface.associatedUser) {
            map.set('associatedUser', productsPaginableRequestInterface.associatedUser);
        }
        if (!!productsPaginableRequestInterface.services) {
            map.set('services', productsPaginableRequestInterface.services);
        }
        return super.get(reduceToMap(map), loader);
    }
};
ProductsPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ProductsPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function ProductsPaginableRestService_Factory() { return new ProductsPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: ProductsPaginableRestService, providedIn: "root" });
ProductsPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ProductsPaginableRestService);

let ProductsRestService = class ProductsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/catalog';
    }
    retrieveProducts(productsPaginableRequestInterface) {
        const localMap = new Map();
        localMap.set('localUnity', String(productsPaginableRequestInterface.localUnity));
        return super.get(reduceToMap(localMap), true, '/user');
    }
    retrieveProductbyId(productId) {
        return super.retrieveAll({
            id: String(productId)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    retrieveProductsForService(servicesmallpayId) {
        const localMap = new Map();
        localMap.set('servicesmallpayId', String(servicesmallpayId));
        return super.get(reduceToMap(localMap), true, '/user');
    }
    createProduct(product) {
        return super.post(product);
    }
    createGenericProduct(product) {
        return super.post(product, '/generalProduct');
    }
    setSelfProduct(product) {
        return super.post(product, '/selfProduct');
    }
    updateProduct(product) {
        return super.put(product);
    }
    deleteProduct(id) {
        return super.delete(id);
    }
};
ProductsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ProductsRestService.ɵprov = ɵɵdefineInjectable({ factory: function ProductsRestService_Factory() { return new ProductsRestService(ɵɵinject(HttpCommunicationsService)); }, token: ProductsRestService, providedIn: "root" });
ProductsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ProductsRestService);

let SetSelfProductRestService = class SetSelfProductRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/catalog';
    }
    setSelfProduct(product) {
        return super.post(product, '/selfProduct');
    }
    hasSelfProduct(localUnity) {
        const localMap = new Map();
        localMap.set('localUnity', String(localUnity));
        return super.get(reduceToMap(localMap), true, '/hasSelfProduct');
    }
};
SetSelfProductRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SetSelfProductRestService.ɵprov = ɵɵdefineInjectable({ factory: function SetSelfProductRestService_Factory() { return new SetSelfProductRestService(ɵɵinject(HttpCommunicationsService)); }, token: SetSelfProductRestService, providedIn: "root" });
SetSelfProductRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SetSelfProductRestService);

let ReceiptsPaginableRestService = class ReceiptsPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/payment';
    }
    retrieveReceipts(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                case 'localUnity':
                    partial[key] = `${param[key]}`;
                    break;
                case 'paidInCash':
                    if (param.paidInCash !== undefined) {
                        partial[key] = param[key] === null ? '' : param[key];
                    }
                    break;
                case 'nominative':
                    if (!!param.nominative) {
                        partial['clientName'] = param.nominative;
                    }
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
ReceiptsPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ReceiptsPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function ReceiptsPaginableRestService_Factory() { return new ReceiptsPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: ReceiptsPaginableRestService, providedIn: "root" });
ReceiptsPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ReceiptsPaginableRestService);

let ReceiptsRestService = class ReceiptsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/payment';
    }
    retrieveReceiptById(id) {
        return super.retrieveAll({
            id: String(id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    downloadCsv(filters) {
        const param = this.produceFilterMap(filters);
        return super.get(param, true, '/export', 'blob');
    }
    produceFilterMap(param) {
        return Object.keys(param).reduce((partial, key) => {
            if (!!param[key]) {
                partial[key] = `${param[key]}`;
            }
            return partial;
        }, {});
    }
    proceedToPayUnpaidInstallments(param) {
        return super.put(param, '/proceedToPayUnpaidInstallments');
    }
};
ReceiptsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ReceiptsRestService.ɵprov = ɵɵdefineInjectable({ factory: function ReceiptsRestService_Factory() { return new ReceiptsRestService(ɵɵinject(HttpCommunicationsService)); }, token: ReceiptsRestService, providedIn: "root" });
ReceiptsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ReceiptsRestService);

let MarketTicketMessageRestService = class MarketTicketMessageRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticketmessage';
    }
    retrieveTicketMessages(id) {
        return super.retrieveAll({ ticketId: String(id) }, '/fromTicketId');
    }
    sendTicketMessage(ticketMessage) {
        return super.post(ticketMessage);
    }
};
MarketTicketMessageRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketTicketMessageRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketTicketMessageRestService_Factory() { return new MarketTicketMessageRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketTicketMessageRestService, providedIn: "root" });
MarketTicketMessageRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketTicketMessageRestService);

let MarketTicketPaginableRestService = class MarketTicketPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticket';
    }
    retrieveTickets(paginableReqInterface) {
        const map = new Map();
        map.set('size', String(paginableReqInterface.size));
        map.set('page', String(paginableReqInterface.page));
        if (!!paginableReqInterface.sortBy) {
            map.set('sortBy', paginableReqInterface.sortBy);
        }
        if (!!paginableReqInterface.sortingMethod) {
            map.set('sortingMethod', paginableReqInterface.sortingMethod);
        }
        if (!!paginableReqInterface.dateStart) {
            map.set('dateStart', paginableReqInterface.dateStart);
        }
        if (!!paginableReqInterface.dateStop) {
            map.set('dateStop', paginableReqInterface.dateStop);
        }
        if (!!paginableReqInterface.object) {
            map.set('object', paginableReqInterface.object);
        }
        if (!!paginableReqInterface.state) {
            map.set('state', paginableReqInterface.state);
        }
        return super.get(reduceToMap(map), false);
    }
};
MarketTicketPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketTicketPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketTicketPaginableRestService_Factory() { return new MarketTicketPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketTicketPaginableRestService, providedIn: "root" });
MarketTicketPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketTicketPaginableRestService);

let MarketTicketRestService = class MarketTicketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticket';
    }
    retrieveTicketbyId(id) {
        return super.retrieveAll({
            id: String(id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    deleteTicket(id) {
        return super.delete(id);
    }
    createTicket(ticketRequestInterface) {
        return super.post(ticketRequestInterface);
    }
};
MarketTicketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketTicketRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketTicketRestService_Factory() { return new MarketTicketRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketTicketRestService, providedIn: "root" });
MarketTicketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketTicketRestService);

let SelfPurchaseRestService = class SelfPurchaseRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/selfproduct';
    }
    updateState(productId, state, feedback, operationId) {
        let url = `/updatestate?productId=${productId}&state=${encodeURIComponent(state)}&feedback=${encodeURIComponent(feedback)}`;
        if (operationId) {
            url = url.concat(`&operationId=${encodeURIComponent(operationId)}`);
        }
        return super.put(null, url);
    }
    getPurchaseRequests(request) {
        const params = new Map();
        params.set('size', String(request.size));
        params.set('page', String(request.page));
        params.set('localUnity', String(request.localUnity));
        if (request.status) {
            params.set('status', request.status);
        }
        return super.get(reduceToMap(params));
    }
    handleError(error) {
        return throwError(() => error);
    }
};
SelfPurchaseRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SelfPurchaseRestService.ɵprov = ɵɵdefineInjectable({ factory: function SelfPurchaseRestService_Factory() { return new SelfPurchaseRestService(ɵɵinject(HttpCommunicationsService)); }, token: SelfPurchaseRestService, providedIn: "root" });
SelfPurchaseRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SelfPurchaseRestService);

let SellConfigurationRestService = class SellConfigurationRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/sellconfiguration';
    }
    retrievePerpetualCheck(gatewayId) {
        return super.get({ gatewayId: `${gatewayId}` }, true, '/checkPerpetualSell');
    }
    retrieveSellConfigurationByGatewayId(gatewayId) {
        return super.retrieveAll({ gatewayId: `${gatewayId}` }).pipe(map(response => response[0]));
    }
};
SellConfigurationRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SellConfigurationRestService.ɵprov = ɵɵdefineInjectable({ factory: function SellConfigurationRestService_Factory() { return new SellConfigurationRestService(ɵɵinject(HttpCommunicationsService)); }, token: SellConfigurationRestService, providedIn: "root" });
SellConfigurationRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SellConfigurationRestService);

let PaymentMethodRestService = class PaymentMethodRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentmethod';
    }
    changePaymentMethod(clientId, serviceId) {
        return super.get({ clientId: `${clientId}`, serviceId: `${serviceId}` }, true, '/change');
    }
};
PaymentMethodRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentMethodRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentMethodRestService_Factory() { return new PaymentMethodRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentMethodRestService, providedIn: "root" });
PaymentMethodRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentMethodRestService);

let SellPreviewRestService = class SellPreviewRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/public/api';
    }
    retrieveOperationInfo(idOperation, processCompleted) {
        return super.get({ idOperation: `${idOperation}`, processCompleted: `${processCompleted}` }, true, '/retrievePaymentInformations');
    }
    executePayment(payment) {
        return super.post(payment, '/executePayment');
    }
    attachPaymentMethod(payment) {
        return super.post(payment, '/paymentmethod/attach');
    }
    intPOSPayment(posIntRequest) {
        return super.post(posIntRequest, '/payment/initializePOS');
    }
    executePolling(operationId) {
        return super.get({ operationId }, false, '/POSPaymentStatus');
    }
    createRecurrences(body) {
        return super.post(body, `/sites/${body.path.domain}/recurrences/${body.path.paymentId}`);
    }
};
SellPreviewRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SellPreviewRestService.ɵprov = ɵɵdefineInjectable({ factory: function SellPreviewRestService_Factory() { return new SellPreviewRestService(ɵɵinject(HttpCommunicationsService)); }, token: SellPreviewRestService, providedIn: "root" });
SellPreviewRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SellPreviewRestService);

let SellRestService = class SellRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/sell';
    }
    sendPaymentLinkViaMail(sellConfiguration) {
        return super.post(sellConfiguration, '/sendPayment');
    }
    payWithPos(sellConfiguration) {
        return super.post(sellConfiguration);
    }
    openSellingPortal(sellConfiguration) {
        return super.post(sellConfiguration, '/requestPayment');
    }
    listenToSellingState(paymentId) {
        return super.get({ size: `${paymentId}` }, false, '/paymentState');
    }
    regeneratePayment(paymentId) {
        return super.put({ id: paymentId }, '/regeneratePayment');
    }
    resendPaymentMail(operationId) {
        return super.put({ operationId: operationId }, '/resendPaymentMail');
    }
    executePolling(operationId) {
        return super.get({ operationId }, false, '/POSPaymentStatus');
    }
    sellWithPos(sellConfiguration) {
        return super.post(sellConfiguration, '/initPOSPayment');
    }
    sellWithQROnPos(sellConfiguration) {
        return super.post(sellConfiguration, '/generateQROnPOS');
    }
};
SellRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SellRestService.ɵprov = ɵɵdefineInjectable({ factory: function SellRestService_Factory() { return new SellRestService(ɵɵinject(HttpCommunicationsService)); }, token: SellRestService, providedIn: "root" });
SellRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SellRestService);

let ServiceInstallmentsPaginableRestService = class ServiceInstallmentsPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/servicesmallpay';
    }
    retrieveServiceInstallments(servicesmallpayPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(servicesmallpayPaginableRequestInterface.size));
        map.set('page', String(servicesmallpayPaginableRequestInterface.page));
        if (!!servicesmallpayPaginableRequestInterface.sortBy) {
            map.set('sortBy', servicesmallpayPaginableRequestInterface.sortBy);
        }
        if (!!servicesmallpayPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', servicesmallpayPaginableRequestInterface.sortingMethod);
        }
        return super.get(reduceToMap(map), false);
    }
};
ServiceInstallmentsPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ServiceInstallmentsPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function ServiceInstallmentsPaginableRestService_Factory() { return new ServiceInstallmentsPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: ServiceInstallmentsPaginableRestService, providedIn: "root" });
ServiceInstallmentsPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ServiceInstallmentsPaginableRestService);

let ServicesmallpayPaginableRestService = class ServicesmallpayPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/servicesmallpay';
    }
    retrieveServices(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                case 'associated':
                    if (typeof param.associated === 'boolean') {
                        partial[key] = `${param.associated}`;
                    }
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
ServicesmallpayPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ServicesmallpayPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function ServicesmallpayPaginableRestService_Factory() { return new ServicesmallpayPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: ServicesmallpayPaginableRestService, providedIn: "root" });
ServicesmallpayPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ServicesmallpayPaginableRestService);

let ServicesmallpayRestService = class ServicesmallpayRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/servicesmallpay';
    }
    getAllServices() {
        return super.retrieveAll();
    }
    retrieveServicesForLocalUnities(localUnities) {
        const localMap = new Map();
        localMap.set('localUnity', String(localUnities));
        return super.get(reduceToMap(localMap));
    }
    retrieveNewServicesForLocalUnities(localUnities) {
        const localMap = new Map();
        localMap.set('localUnity', String(localUnities));
        return super.get(reduceToMap(localMap), true, '/sell');
    }
    getAvailableGeneralTypes() {
        return super.get(null, true, '/generaltype');
    }
    retrieveServiceById(id) {
        return super.retrieveAll({ id: `${id}` })
            .pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    updateService(param) {
        return super.put(param);
    }
    updateNameService(param) {
        return super.put(param, '/updateName');
    }
};
ServicesmallpayRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
ServicesmallpayRestService.ɵprov = ɵɵdefineInjectable({ factory: function ServicesmallpayRestService_Factory() { return new ServicesmallpayRestService(ɵɵinject(HttpCommunicationsService)); }, token: ServicesmallpayRestService, providedIn: "root" });
ServicesmallpayRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ServicesmallpayRestService);

let StatisticsmallpayRestService = class StatisticsmallpayRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/statistics/';
    }
    retrieveStatisticsSales(param) {
        return super.get(this.buildParamForMarketStatistics(param), true, 'sales');
    }
    retrieveStatisticsPayments(param) {
        return super.get(this.buildParamForMarketStatistics(param), true, 'payments');
    }
    retrieveStatisticsFutureRates(param) {
        return super.get(this.buildParamForMarketStatistics(param), true, 'futureRates');
    }
    retrieveMerchantPayments(param) {
        return super.get(param, true, 'merchantsPayments');
    }
    retrieveMerchants() {
        return super.get(null, true, 'merchants');
    }
    retrieveMonthlyTrend(param) {
        return super.get(param, true, 'monthlyTrend');
    }
    exportPaymentXLS(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            if (!!param[key]) {
                partial[key] = `${param[key]}`;
            }
            return partial;
        }, {});
        return super.get(map, true, 'exportPaymentsXLS');
    }
    buildParamForMarketStatistics(param) {
        return Object.keys(param).reduce((partial, key) => {
            if (key === 'localUnity' && param.localUnity.length > 0) {
                partial[key] = `${param[key]}`;
            }
            else if (!!param[key]) {
                partial[key] = `${param[key]}`;
            }
            return partial;
        }, {});
    }
};
StatisticsmallpayRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
StatisticsmallpayRestService.ɵprov = ɵɵdefineInjectable({ factory: function StatisticsmallpayRestService_Factory() { return new StatisticsmallpayRestService(ɵɵinject(HttpCommunicationsService)); }, token: StatisticsmallpayRestService, providedIn: "root" });
StatisticsmallpayRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], StatisticsmallpayRestService);

let MarketUsersPaginableRestService = class MarketUsersPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/user';
    }
    retrieveUsers(usersPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(usersPaginableRequestInterface.size));
        map.set('page', String(usersPaginableRequestInterface.page));
        map.set('localUnity', String(usersPaginableRequestInterface.localUnity));
        if (!!usersPaginableRequestInterface.sortBy) {
            map.set('sortBy', usersPaginableRequestInterface.sortBy);
        }
        if (!!usersPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', usersPaginableRequestInterface.sortingMethod);
        }
        if (!!usersPaginableRequestInterface.name) {
            map.set('name', usersPaginableRequestInterface.name);
        }
        if (!!usersPaginableRequestInterface.localUnityName) {
            map.set('localUnityName', usersPaginableRequestInterface.localUnityName);
        }
        if (!!usersPaginableRequestInterface.authorities) {
            map.set('authorities', usersPaginableRequestInterface.authorities);
        }
        if (!!usersPaginableRequestInterface.surname) {
            map.set('surname', usersPaginableRequestInterface.surname);
        }
        if (!!usersPaginableRequestInterface.email) {
            map.set('email', usersPaginableRequestInterface.email);
        }
        if (!!usersPaginableRequestInterface.address) {
            map.set('address', usersPaginableRequestInterface.address);
        }
        if (!!usersPaginableRequestInterface.stato) {
            map.set('stato', usersPaginableRequestInterface.stato);
        }
        return super.get(reduceToMap(map), false);
    }
};
MarketUsersPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketUsersPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketUsersPaginableRestService_Factory() { return new MarketUsersPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketUsersPaginableRestService, providedIn: "root" });
MarketUsersPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketUsersPaginableRestService);

let MarketUsersRestService = class MarketUsersRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/user';
    }
    retrieveUserbyId(usersRequestInterface) {
        return super.retrieveAll({
            id: String(usersRequestInterface.id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    retrieveCurrentUser() {
        return super.retrieveAll(null, '/currentUser').pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    retrieveUsersForLocalUnities(localUnities) {
        const localMap = new Map();
        localMap.set('localUnities', String(localUnities));
        return super.get(reduceToMap(localMap));
    }
    updateUserPassword(newPassword) {
        return super.put({ password: newPassword }, '/updatePassword');
    }
    createUser(usersRequestInterface) {
        return super.post(usersRequestInterface);
    }
    updateUser(usersRequestInterface) {
        return super.put(usersRequestInterface);
    }
    deleteUser(id) {
        return super.delete(id);
    }
    enableUser(id) {
        return super.put({ id }, '/activate');
    }
};
MarketUsersRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MarketUsersRestService.ɵprov = ɵɵdefineInjectable({ factory: function MarketUsersRestService_Factory() { return new MarketUsersRestService(ɵɵinject(HttpCommunicationsService)); }, token: MarketUsersRestService, providedIn: "root" });
MarketUsersRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MarketUsersRestService);

const userStates = [
    'ATTIVO',
    'NON ATTIVO'
];

let CreateAdvService = class CreateAdvService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/notices/generatePdfNotices';
    }
    createNewAdv(request, applicationId) {
        const formData = new FormData();
        formData.append('pdfFile', request.pdfFile, request.documentId);
        formData.append('documentId', request.documentId);
        formData.append('applicationId', applicationId);
        return super.post(formData);
    }
};
CreateAdvService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
CreateAdvService.ɵprov = ɵɵdefineInjectable({ factory: function CreateAdvService_Factory() { return new CreateAdvService(ɵɵinject(HttpCommunicationsService)); }, token: CreateAdvService, providedIn: "root" });
CreateAdvService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], CreateAdvService);

let DashboardSupportRestService = class DashboardSupportRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/dashboard';
    }
    retrieveDashboard() {
        return super.retrieveAll();
    }
};
DashboardSupportRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
DashboardSupportRestService.ɵprov = ɵɵdefineInjectable({ factory: function DashboardSupportRestService_Factory() { return new DashboardSupportRestService(ɵɵinject(HttpCommunicationsService)); }, token: DashboardSupportRestService, providedIn: "root" });
DashboardSupportRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], DashboardSupportRestService);

let GatewaySmallpayRestService = class GatewaySmallpayRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/gatewaysmallpay';
    }
    retrieveGatewaySmallPay(applicationId) {
        return super.retrieveAll(null, '/available', new HttpHeaders({ applicationId }));
    }
};
GatewaySmallpayRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
GatewaySmallpayRestService.ɵprov = ɵɵdefineInjectable({ factory: function GatewaySmallpayRestService_Factory() { return new GatewaySmallpayRestService(ɵɵinject(HttpCommunicationsService)); }, token: GatewaySmallpayRestService, providedIn: "root" });
GatewaySmallpayRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], GatewaySmallpayRestService);

let MerchantGatewayRestService = class MerchantGatewayRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/gateway';
    }
    retrieveGateways(param) {
        const localMap = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(localMap, false, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
    getMerchantActiveGateways(merchantId, applicationId) {
        const params = merchantId ? { merchantId: `${merchantId}`, applicationId } : null;
        return super.retrieveAll(params, '/activeGateways');
    }
    getMerchantStripeGateways(merchantId) {
        const params = merchantId ? { merchantId: `${merchantId}` } : null;
        return super.retrieveAll(params, '/getStripe');
    }
    updateGateways(gatewaysRequestInterface) {
        return super.putAll([...gatewaysRequestInterface], '/updateGateways');
    }
    updateGateway(gatewaysRequestInterface) {
        return super.put(gatewaysRequestInterface);
    }
    deleteGateway(id, applicationId) {
        return super.delete(id, new HttpHeaders({ applicationId }));
    }
    retrieveGatewayById(id, applicationId) {
        return super.retrieveAll({
            id: `${id}`
        }, '', new HttpHeaders({ applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
};
MerchantGatewayRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantGatewayRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantGatewayRestService_Factory() { return new MerchantGatewayRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantGatewayRestService, providedIn: "root" });
MerchantGatewayRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantGatewayRestService);

let MerchantLocalUnitsRestService = class MerchantLocalUnitsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/localunity';
    }
    retrieveLocalUnitsById(merchantLocalUnitsRequestInterface) {
        return super.retrieveAll({ id: `${merchantLocalUnitsRequestInterface.id}` }, '', new HttpHeaders({ applicationId: merchantLocalUnitsRequestInterface.applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    retrieveLocalUnitsByMerchantId(merchantLocalUnitsRequestInterface) {
        return super.retrieveAll({ id: `${merchantLocalUnitsRequestInterface.id}` }, '/findByMerchant', new HttpHeaders({ applicationId: merchantLocalUnitsRequestInterface.applicationId }));
    }
    retrieveLocalUnits(param) {
        const localMap = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(localMap, false, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
    createMerchantLocalUnit(merchantLocalUnitsRequestInterface) {
        return super.post(merchantLocalUnitsRequestInterface);
    }
    updateMerchantLocalUnit(merchantLocalUnitsRequestInterface) {
        return super.put(merchantLocalUnitsRequestInterface);
    }
};
MerchantLocalUnitsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantLocalUnitsRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantLocalUnitsRestService_Factory() { return new MerchantLocalUnitsRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantLocalUnitsRestService, providedIn: "root" });
MerchantLocalUnitsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantLocalUnitsRestService);

let MerchantPhysicalPosRestService = class MerchantPhysicalPosRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/physicalpos';
    }
    retrievePosForLocalUnit(requestInterface) {
        return super.retrieveAll({ localUnity: String(requestInterface.localUnity) }, '/localunity', new HttpHeaders({ applicationId: requestInterface.applicationId }));
    }
};
MerchantPhysicalPosRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantPhysicalPosRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantPhysicalPosRestService_Factory() { return new MerchantPhysicalPosRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantPhysicalPosRestService, providedIn: "root" });
MerchantPhysicalPosRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantPhysicalPosRestService);

let MerchantPosOnboardingRestService = class MerchantPosOnboardingRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/posonboarding';
    }
    insertLead(request) {
        return super.post(request, '/insert', true, new HttpHeaders({ applicationId: request.applicationId ? request.applicationId : null }));
    }
    retrieveLeads(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            if (!!param[key]) {
                partial[key] = `${param[key]}`;
            }
            return partial;
        }, {});
        return super.retrieveAll(map, '/getLeads', new HttpHeaders({ applicationId: param.applicationId ? param.applicationId : null }));
    }
};
MerchantPosOnboardingRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantPosOnboardingRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantPosOnboardingRestService_Factory() { return new MerchantPosOnboardingRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantPosOnboardingRestService, providedIn: "root" });
MerchantPosOnboardingRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantPosOnboardingRestService);

let MerchantServicesPaginableRestService = class MerchantServicesPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/servicesmallpay';
    }
    retrieveMerchantServices(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'page':
                case 'size':
                case 'merchant':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
            }
            return partial;
        }, {});
        return super.get(map, false, '', null, new HttpHeaders({ applicationId: param.applicationId ? param.applicationId : null }));
    }
};
MerchantServicesPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantServicesPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantServicesPaginableRestService_Factory() { return new MerchantServicesPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantServicesPaginableRestService, providedIn: "root" });
MerchantServicesPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantServicesPaginableRestService);

let MerchantServicesRestService = class MerchantServicesRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/servicesmallpay';
    }
    retrieveServiceById(merchantServicesRequestInterface, applicationId) {
        return super.retrieveAll({
            id: String(merchantServicesRequestInterface.id),
        }, '', new HttpHeaders({ applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    automaticallyEnableService(payload) {
        switch (payload.paymentType) {
            case 'cardActivation':
                return super.put(payload.param);
            case 'cashActivation':
                return super.put(payload.param, '/cashActivation');
            case 'freeActivation':
            default:
                return super.put(payload.param, '/freeActivation');
        }
    }
};
MerchantServicesRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantServicesRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantServicesRestService_Factory() { return new MerchantServicesRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantServicesRestService, providedIn: "root" });
MerchantServicesRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantServicesRestService);

let MerchantUsersPaginableRestService = class MerchantUsersPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/userMerchant';
    }
    retrieveMerchantUsers(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                case 'merchant':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
};
MerchantUsersPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantUsersPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantUsersPaginableRestService_Factory() { return new MerchantUsersPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantUsersPaginableRestService, providedIn: "root" });
MerchantUsersPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantUsersPaginableRestService);

let MerchantsConfigurationRestService = class MerchantsConfigurationRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/merchantConfiguration';
    }
    updateMerchantConfiguration(param) {
        return super.put(param);
    }
    retrieveMerchantConfigurations(param) {
        return super.get({ ['merchantId']: `${param.merchantId}` }, true, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
};
MerchantsConfigurationRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantsConfigurationRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantsConfigurationRestService_Factory() { return new MerchantsConfigurationRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantsConfigurationRestService, providedIn: "root" });
MerchantsConfigurationRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantsConfigurationRestService);

let MerchantsPaginableRestService = class MerchantsPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/merchant';
    }
    retrieveMerchants(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'page':
                case 'size':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
            }
            return partial;
        }, {});
        return super.get(map);
    }
};
MerchantsPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantsPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantsPaginableRestService_Factory() { return new MerchantsPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantsPaginableRestService, providedIn: "root" });
MerchantsPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantsPaginableRestService);

let MerchantsRestService = class MerchantsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/merchant';
    }
    retrieveMerchantbyId(merchantsRequestInterface) {
        return super.retrieveAll({
            id: String(merchantsRequestInterface.id)
        }, '', new HttpHeaders({ applicationId: merchantsRequestInterface.applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    createMerchant(merchantsRequestInterface) {
        return super.post(merchantsRequestInterface);
    }
    updateMerchant(merchantsRequestInterface) {
        return super.put(merchantsRequestInterface);
    }
    activateMerchant(merchantId) {
        return super.put(merchantId, '/activateMerchant');
    }
    setTemporallyOperative(merchantServicesRequestInterface) {
        return super.put(merchantServicesRequestInterface, '/temporarilyOperational');
    }
    updateGatewaySmallPay(param) {
        return super.put(param, '/defaultGatewaySmallpay');
    }
};
MerchantsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
MerchantsRestService.ɵprov = ɵɵdefineInjectable({ factory: function MerchantsRestService_Factory() { return new MerchantsRestService(ɵɵinject(HttpCommunicationsService)); }, token: MerchantsRestService, providedIn: "root" });
MerchantsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], MerchantsRestService);

const merchantStates = [
    'ATTIVO',
    'CONCLUSO',
    'DISATTIVATO',
    'IN ATTIVAZIONE',
    'INSOLVENTE',
    'OPERATIVO',
    'TEMPORANEAMENTE OPERATIVO',
];

let NotificationSupportRestService = class NotificationSupportRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/notifications';
    }
    retrieveNotifications(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map);
    }
};
NotificationSupportRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
NotificationSupportRestService.ɵprov = ɵɵdefineInjectable({ factory: function NotificationSupportRestService_Factory() { return new NotificationSupportRestService(ɵɵinject(HttpCommunicationsService)); }, token: NotificationSupportRestService, providedIn: "root" });
NotificationSupportRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], NotificationSupportRestService);

const gatewayChannelList = [
    'CONSORZIO',
    'NEXI',
    'NEXI_PAYPAL',
    'NEXI_3_PAYPAL',
    'NEXI_PAGODIL',
    'STRIPE',
    'STRIPE_SDD',
    'STRIPE_KLARNA',
    'STRIPE_ALMA',
    'STRIPE_SCALAPAY',
    'SCALAPAY',
    'APPPAGO',
    'KLARNA',
    'QOMODO',
];

const paymentStates = [
    'PAGATO',
    'DA PAGARE',
    'ELIMINATO',
    'IN ATTESA GTW',
    'IN ELABORAZIONE',
    'INSOLUTO',
    'RIFIUTATO'
];
const paymentStatesOrderedForIncassi = [
    'PAGATO',
    'DA PAGARE',
    'ELIMINATO',
    'IN ATTESA GTW',
    'IN ELABORAZIONE',
    'INSOLUTO',
    'RIFIUTATO'
];

var DocumentStatus;
(function (DocumentStatus) {
    DocumentStatus[DocumentStatus["Presa in carico"] = 1] = "Presa in carico";
    DocumentStatus[DocumentStatus["Errore elaborazione"] = 2] = "Errore elaborazione";
    DocumentStatus[DocumentStatus["Inviata"] = 3] = "Inviata";
    DocumentStatus[DocumentStatus["Scartata"] = 4] = "Scartata";
    DocumentStatus[DocumentStatus["Non consegnata"] = 5] = "Non consegnata";
    DocumentStatus[DocumentStatus["Recapito impossibile"] = 6] = "Recapito impossibile";
    DocumentStatus[DocumentStatus["Consegnata"] = 7] = "Consegnata";
    DocumentStatus[DocumentStatus["Accettata"] = 8] = "Accettata";
    DocumentStatus[DocumentStatus["Rifiutata"] = 9] = "Rifiutata";
    DocumentStatus[DocumentStatus["Docorrenza termini"] = 10] = "Docorrenza termini";
})(DocumentStatus || (DocumentStatus = {}));

let PaidUnpaidInstallmentsRestService = class PaidUnpaidInstallmentsRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentsmallpay';
    }
    payUnpaidInstallments(param) {
        return super.put(param, '/payUnpaidInstallments');
    }
    proceedToPayUnpaidInstallments(param) {
        return super.put(param, '/proceedToPayUnpaidInstallments');
    }
};
PaidUnpaidInstallmentsRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaidUnpaidInstallmentsRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaidUnpaidInstallmentsRestService_Factory() { return new PaidUnpaidInstallmentsRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaidUnpaidInstallmentsRestService, providedIn: "root" });
PaidUnpaidInstallmentsRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaidUnpaidInstallmentsRestService);

let PaymentFeesSupportPaginableRestService = class PaymentFeesSupportPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/rate';
    }
    retrieveFees(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'page':
                case 'size':
                case 'rataSmallpayId':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
            }
            return partial;
        }, {});
        return super.get(map, false, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
};
PaymentFeesSupportPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentFeesSupportPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentFeesSupportPaginableRestService_Factory() { return new PaymentFeesSupportPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentFeesSupportPaginableRestService, providedIn: "root" });
PaymentFeesSupportPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentFeesSupportPaginableRestService);

let PaymentsSupportBillingRestService = class PaymentsSupportBillingRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/fatturazione';
    }
    bill(request) {
        return super.post(request, '/creazioneFatturaNotaCredito');
    }
    downloadDocument(documentId, applicationId) {
        if (applicationId) {
            return super.retrieveAll({ documentId: `${documentId}` }, '/findByDocument', new HttpHeaders({ applicationId })).pipe(map(result => !!result && result.length > 0 ? result[0] : undefined));
        }
        return super.retrieveAll({ documentId: `${documentId}` }, '/findByDocument').pipe(map(result => !!result && result.length > 0 ? result[0] : undefined));
    }
    relaunchBill(id, applicationId) {
        return super.post({ id, applicationId }, '/riaccodaFatturaNotaCredito');
    }
};
PaymentsSupportBillingRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentsSupportBillingRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentsSupportBillingRestService_Factory() { return new PaymentsSupportBillingRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentsSupportBillingRestService, providedIn: "root" });
PaymentsSupportBillingRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentsSupportBillingRestService);

let PaymentsSupportPaginableRestService = class PaymentsSupportPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentsmallpay';
    }
    retrievePayments(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                case 'paidInCash':
                    if (param.paidInCash !== undefined) {
                        partial[key] = param[key] === null ? '' : param[key];
                    }
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
PaymentsSupportPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentsSupportPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentsSupportPaginableRestService_Factory() { return new PaymentsSupportPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentsSupportPaginableRestService, providedIn: "root" });
PaymentsSupportPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentsSupportPaginableRestService);

let PaymentsSupportRestService = class PaymentsSupportRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/paymentsmallpay';
    }
    retrievePaymentbyId(payment) {
        return super.retrieveAll({ id: String(payment.id) }, '', new HttpHeaders({ applicationId: payment.applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    requestPayment(payment) {
        return super.put(payment, '/requestPayment');
    }
    payPerCashDesk(payment) {
        return super.put(payment, '/paymentConfirmed');
    }
    activateMerchant(merchantId) {
        return super.put(merchantId, '/activateMerchant');
    }
    initSddGateway(request) {
        return super.post(request, '/requestMerchantGatewaySdd');
    }
};
PaymentsSupportRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
PaymentsSupportRestService.ɵprov = ɵɵdefineInjectable({ factory: function PaymentsSupportRestService_Factory() { return new PaymentsSupportRestService(ɵɵinject(HttpCommunicationsService)); }, token: PaymentsSupportRestService, providedIn: "root" });
PaymentsSupportRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], PaymentsSupportRestService);

let RatesmallpaySupportPaginableRestService = class RatesmallpaySupportPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ratesmallpay';
    }
    retrieveRatesmallpay(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'page':
                case 'size':
                case 'idPayment':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
            }
            return partial;
        }, {});
        return super.get(map, false, '', null, new HttpHeaders({ applicationId: param.applicationId }));
    }
};
RatesmallpaySupportPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
RatesmallpaySupportPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function RatesmallpaySupportPaginableRestService_Factory() { return new RatesmallpaySupportPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: RatesmallpaySupportPaginableRestService, providedIn: "root" });
RatesmallpaySupportPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RatesmallpaySupportPaginableRestService);

let RatesmallpaySupportRestService = class RatesmallpaySupportRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ratesmallpay';
    }
    retrieveRatePerService(servicesmallpayId, applicationId) {
        const map = new Map();
        map.set('servicesmallpayId', String(servicesmallpayId));
        return super.get(reduceToMap(map), true, '', null, new HttpHeaders({ applicationId }));
    }
    updateRate(updatedRata) {
        return super.put(updatedRata, '/updateRatesByService');
    }
};
RatesmallpaySupportRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
RatesmallpaySupportRestService.ɵprov = ɵɵdefineInjectable({ factory: function RatesmallpaySupportRestService_Factory() { return new RatesmallpaySupportRestService(ɵɵinject(HttpCommunicationsService)); }, token: RatesmallpaySupportRestService, providedIn: "root" });
RatesmallpaySupportRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RatesmallpaySupportRestService);

let SupportTicketMessageRestService = class SupportTicketMessageRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticketmessage';
    }
    retrieveTicketMessages(id, applicationId) {
        return super.retrieveAll({ ticketId: String(id) }, '/fromTicketId', new HttpHeaders({ applicationId }));
    }
    sendTicketMessage(ticketMessage) {
        return super.post(ticketMessage);
    }
};
SupportTicketMessageRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SupportTicketMessageRestService.ɵprov = ɵɵdefineInjectable({ factory: function SupportTicketMessageRestService_Factory() { return new SupportTicketMessageRestService(ɵɵinject(HttpCommunicationsService)); }, token: SupportTicketMessageRestService, providedIn: "root" });
SupportTicketMessageRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SupportTicketMessageRestService);

let SupportTicketPaginableRestService = class SupportTicketPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticket';
    }
    retrieveTickets(param) {
        const map = Object.keys(param).reduce((partial, key) => {
            switch (key) {
                case 'size':
                case 'page':
                    partial[key] = `${param[key]}`;
                    break;
                default:
                    if (!!param[key]) {
                        partial[key] = `${param[key]}`;
                    }
                    break;
            }
            return partial;
        }, {});
        return super.get(map, false);
    }
};
SupportTicketPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SupportTicketPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function SupportTicketPaginableRestService_Factory() { return new SupportTicketPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: SupportTicketPaginableRestService, providedIn: "root" });
SupportTicketPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SupportTicketPaginableRestService);

let SupportTicketRestService = class SupportTicketRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/ticket';
    }
    retrieveTicketbyId(id, applicationId) {
        return super.retrieveAll({
            id: String(id)
        }, '', new HttpHeaders({ applicationId })).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    deleteTicket(id, applicationId) {
        return super.delete(id, new HttpHeaders({ applicationId }));
    }
    updateTicket(ticketRequestInterface) {
        return super.put(ticketRequestInterface);
    }
};
SupportTicketRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
SupportTicketRestService.ɵprov = ɵɵdefineInjectable({ factory: function SupportTicketRestService_Factory() { return new SupportTicketRestService(ɵɵinject(HttpCommunicationsService)); }, token: SupportTicketRestService, providedIn: "root" });
SupportTicketRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], SupportTicketRestService);

let UsersPaginableRestService = class UsersPaginableRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/user';
    }
    retrieveUsers(usersPaginableRequestInterface) {
        const map = new Map();
        map.set('size', String(usersPaginableRequestInterface.size));
        map.set('page', String(usersPaginableRequestInterface.page));
        if (!!usersPaginableRequestInterface.sortBy) {
            map.set('sortBy', usersPaginableRequestInterface.sortBy);
        }
        if (!!usersPaginableRequestInterface.sortingMethod) {
            map.set('sortingMethod', usersPaginableRequestInterface.sortingMethod);
        }
        if (!!usersPaginableRequestInterface.name) {
            map.set('name', usersPaginableRequestInterface.name);
        }
        if (!!usersPaginableRequestInterface.surname) {
            map.set('surname', usersPaginableRequestInterface.surname);
        }
        if (!!usersPaginableRequestInterface.email) {
            map.set('email', usersPaginableRequestInterface.email);
        }
        if (!!usersPaginableRequestInterface.address) {
            map.set('address', usersPaginableRequestInterface.address);
        }
        return super.get(reduceToMap(map), false);
    }
};
UsersPaginableRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
UsersPaginableRestService.ɵprov = ɵɵdefineInjectable({ factory: function UsersPaginableRestService_Factory() { return new UsersPaginableRestService(ɵɵinject(HttpCommunicationsService)); }, token: UsersPaginableRestService, providedIn: "root" });
UsersPaginableRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], UsersPaginableRestService);

let UsersRestService = class UsersRestService extends AbstractRestService {
    constructor(httpCommunicationsService) {
        super(httpCommunicationsService);
        this.path = '/user';
    }
    retrieveUserbyId(usersRequestInterface) {
        return super.retrieveAll({
            id: String(usersRequestInterface.id)
        }).pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    retrieveCurrentUser() {
        return super.retrieveAll(null, '/currentUser').pipe(filter(result => !!result && result.length > 0), map(element => element[0]));
    }
    updateUserPassword(newPassword) {
        return super.put({ password: newPassword }, '/updatePassword');
    }
    createUser(usersRequestInterface) {
        return super.post(usersRequestInterface);
    }
    updateUser(usersRequestInterface) {
        return super.put(usersRequestInterface);
    }
    deleteUser(id) {
        return super.delete(id);
    }
};
UsersRestService.ctorParameters = () => [
    { type: HttpCommunicationsService }
];
UsersRestService.ɵprov = ɵɵdefineInjectable({ factory: function UsersRestService_Factory() { return new UsersRestService(ɵɵinject(HttpCommunicationsService)); }, token: UsersRestService, providedIn: "root" });
UsersRestService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], UsersRestService);

let ExternalRoutingGuardService = class ExternalRoutingGuardService {
    constructor(appState$, userTokenService) {
        this.appState$ = appState$;
        this.userTokenService = userTokenService;
    }
    // tslint:disable-next-line: naming-conventions
    canActivateChild(childRoute, state) {
        return true;
    }
};
ExternalRoutingGuardService.ctorParameters = () => [
    { type: Store },
    { type: UserAuthenticationService }
];
ExternalRoutingGuardService.ɵprov = ɵɵdefineInjectable({ factory: function ExternalRoutingGuardService_Factory() { return new ExternalRoutingGuardService(ɵɵinject(Store), ɵɵinject(UserAuthenticationService)); }, token: ExternalRoutingGuardService, providedIn: "root" });
ExternalRoutingGuardService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], ExternalRoutingGuardService);

let RoutingGuardService = class RoutingGuardService {
    constructor(userTokenService, router) {
        this.userTokenService = userTokenService;
        this.router = router;
    }
    // tslint:disable-next-line: naming-conventions
    canActivateChild(childRoute, state) {
        const isUserLogged = this.userTokenService.isUserLogged();
        if (!isUserLogged) {
            this.router.navigateByUrl('login');
        }
        return isUserLogged;
    }
};
RoutingGuardService.ctorParameters = () => [
    { type: UserAuthenticationService },
    { type: Router }
];
RoutingGuardService.ɵprov = ɵɵdefineInjectable({ factory: function RoutingGuardService_Factory() { return new RoutingGuardService(ɵɵinject(UserAuthenticationService), ɵɵinject(Router)); }, token: RoutingGuardService, providedIn: "root" });
RoutingGuardService = __decorate([
    Injectable({
        providedIn: 'root'
    })
], RoutingGuardService);

let HtmlSanitizerPipe = class HtmlSanitizerPipe {
    constructor(domSanitizer) {
        this.domSanitizer = domSanitizer;
    }
    transform(value) {
        return this.domSanitizer.sanitize(SecurityContext.URL, value);
    }
};
HtmlSanitizerPipe.ctorParameters = () => [
    { type: DomSanitizer }
];
HtmlSanitizerPipe = __decorate([
    Pipe({
        name: 'sanitizeHtml'
    })
], HtmlSanitizerPipe);

let ArchPipesModule = class ArchPipesModule {
};
ArchPipesModule = __decorate([
    NgModule({
        declarations: [HtmlSanitizerPipe],
        imports: [
            CommonModule
        ],
        exports: [HtmlSanitizerPipe]
    })
], ArchPipesModule);

/*
 * Public API Surface of sp-arch-lib
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractComponentClass, AbstractContainerClass, AbstractDirectiveClass, ArchPipesModule, AuthenticationRestService, BootRestService, CreateAdvService, CreateGeneralProductRestService, CustomerInstallmentsByClientIdRestService, CustomerInstallmentsRestService, CustomerPaymentsPaginableRestService, CustomerPaymentsRestService, CustomersPaginableRestService, CustomersRestService, DashboardMarketRestService, DashboardSupportRestService, DocumentStatus, DownloadWordDocsService, ExternalRoutingGuardService, ForgotPasswordService, GatewayRestService, GatewaySmallpayRestService, GeolocationRestService, HtmlSanitizerPipe, HttpCommunicationsModule, HttpCommunicationsService, HttpRequestInterceptorService, HttpResponseInterceptorService, IncrementalJsonStorageService, InternationalizationModule, LocalUnitPaginableRestService, LocalUnitQrCodeRestService, LocalUnitRestService, MarketCatalogImgRestService, MarketMerchantImgRestService, MarketMerchantRestService, MarketTicketMessageRestService, MarketTicketPaginableRestService, MarketTicketRestService, MarketUsersPaginableRestService, MarketUsersRestService, MerchantGatewayRestService, MerchantLocalUnitsRestService, MerchantPhysicalPosRestService, MerchantPosOnboardingRestService, MerchantServicesPaginableRestService, MerchantServicesRestService, MerchantUsersPaginableRestService, MerchantsConfigurationRestService, MerchantsPaginableRestService, MerchantsRestService, NotificationMarketRestService, NotificationSupportRestService, PaidUnpaidInstallmentsRestService, PaymentChannels, PaymentFeesMarketPaginableRestService, PaymentFeesSupportPaginableRestService, PaymentMethodRestService, PaymentServiceMarketRestService, PaymentsMarketPaginableRestService, PaymentsMarketRestService, PaymentsSupportBillingRestService, PaymentsSupportPaginableRestService, PaymentsSupportRestService, PosPaginableRestService, PosRestService, ProductsPaginableRestService, ProductsRestService, RatesmallpayMarketPaginableRestService, RatesmallpaySupportPaginableRestService, RatesmallpaySupportRestService, ReceiptsPaginableRestService, ReceiptsRestService, RegistryCreditCardPaginableRestService, RegistryCreditCardRestService, RoutingGuardService, SelfPurchaseRestService, SellConfigurationRestService, SellPreviewRestService, SellRestService, ServiceInstallmentsPaginableRestService, ServicesmallpayPaginableRestService, ServicesmallpayRestService, SetSelfProductRestService, StatisticsmallpayRestService, SubscriberManager, SupportTicketMessageRestService, SupportTicketPaginableRestService, SupportTicketRestService, TokenInterceptorEffects, UserAuthenticationService, UsersPaginableRestService, UsersRestService, applications, checkToken, customerStates, decrementCount, decrementCountDelayed, decrementPublicCount, deleteToken, gatewayChannelList, incrementCounter, incrementPublicCounter, internalServerError, merchantStates, pageNotFound, paymentStates, paymentStatesOrderedForIncassi, posTypes, reducer, removePublicPageState, retrieveUserTokenInformations, selectLoaderCount, selectPublicLoaderCount, selectUserInformation, selectUserToken, selectUserUsername, setPublicPageState, setUsername, updateToken, userStates, ɵ0, ɵ1, ɵ2, ɵ3, ɵ4, ɵ5, incrementalTranslateHTTPLoaderFactory as ɵa, IncrementalTranslateHTTPLoader as ɵb, SpMissingTranslationHandler as ɵc, AbstractRestService as ɵd };
//# sourceMappingURL=sp-arch-lib.js.map
